import { ChatState, ConnectionState } from '../core/models/events-models';
export class Provider {
    constructor(session) {
        this.providerIdentifier = {
            providerId: '',
            providerName: '',
        };
        this.messageCallbacks = new Map();
        this.ws = null;
        this.purposefulClose = false;
        this.missedPongs = 0;
        this.isTyping = false;
        this.startChatPromise = null;
        this.startChatPromiseHandlers = null;
        this.endChatPromise = null;
        this.endChatPromiseHandlers = null;
        this.session = session;
    }
    startChat(token, providerId, platformSettings, requestParameters) {
        if (this.getWebSocketState() !== WebSocket.CLOSED) {
            throw { code: 'INVALID_CHAT_STATUS', message: 'Websocket already opened.' };
        }
        this.latestToken = token;
        this.sessionId = undefined;
        this.providerIdentifier = { providerId: providerId, providerName: '' };
        this.requestParams = requestParameters;
        this.session.chatInstance.setPlatformSettings(platformSettings);
        this.session.chatInstance.emitStatusChange('', this.providerIdentifier, ConnectionState.CONNECTING, {
            state: ChatState.READY,
        });
        return this.establishConnection();
    }
    closeChat(token) {
        const connectionState = this.session.chatInstance.getCurrentConnectionState();
        if (connectionState === ConnectionState.RECONNECTING) {
            this.endReconnection();
            this.closeWebsocket(true);
            this.cleanUpWebsocket();
            this.cleanUpPromises();
            this.session.chatInstance.emitStatusChange('', this.providerIdentifier, ConnectionState.DISCONNECTED, {
                state: ChatState.COMPLETE,
                endedBy: 'CLIENT',
            });
            return Promise.resolve();
        }
        if (this.getWebSocketState() !== WebSocket.OPEN) {
            return Promise.reject({ code: 'INVALID_CHAT_STATUS', message: 'No open websocket connection' });
        }
        if (this.endChatPromise) {
            return this.endChatPromise;
        }
        this.endChatPromise = new Promise((resolve, reject) => {
            this.endChatPromiseHandlers = { resolve, reject };
        });
        setTimeout(() => {
            if (!this.endChatPromiseHandlers) {
                return;
            }
            this.rejectCloseChatPromise({
                code: 'METHOD_ERROR',
                message: 'No response from websocket connection.',
            });
        }, this.session.chatInstance.getCloseChatTimeout());
        this.latestToken = token;
        this.purposefulClose = true;
        if (this.session.config.useMocks && this.session.config.useMocks.mockClose) {
            setTimeout(() => {
                if (!this.endChatPromiseHandlers) {
                    return;
                }
                this.onMessage({
                    data: JSON.stringify({
                        event: 'statusChange',
                        data: {
                            sessionId: '1234567890',
                            provider: this.providerIdentifier,
                            chatStatus: ChatState.COMPLETE,
                            timestamp: new Date().toLocaleString(),
                        },
                    }),
                });
            }, 500);
        }
        else {
            this.onChatClose();
        }
        return this.endChatPromise;
    }
    sendIsTyping() {
        if (this.getWebSocketState() !== WebSocket.OPEN) {
            return;
        }
        else if (!this.isTyping) {
            this.sendIsTypingHandler();
            this.isTyping = true;
            setTimeout(() => (this.isTyping = false), this.session.chatInstance.getIsTypingInterval());
        }
    }
    sendEvent(token, buttonContent) {
        this.latestToken = token;
        if (!this.session.chatInstance.getChatFeatures().allowEventInput) {
            return Promise.reject({ code: 'EVENT_SEND_DISABLED', message: 'Event input disabled.' });
        }
        else if (!buttonContent || !buttonContent.action.event) {
            return Promise.reject({ code: 'EVENT_SEND_INVALID', message: 'Invalid event.' });
        }
        const id = this.session.chatInstance.getNextMessageId();
        const message = {
            event: 'newEvent',
            data: {
                access_token: token,
                eventName: buttonContent.action.event.name,
                eventLabel: buttonContent.text,
                parameters: buttonContent.action.event.parameters,
                messageId: id.toString(),
            },
        };
        return this.sendUserMessage(id, message);
    }
    sendMessage(token, text) {
        this.latestToken = token;
        if (!this.session.chatInstance.getChatFeatures().allowTextInput) {
            return Promise.reject({ code: 'MESSAGE_SEND_DISABLED', message: 'Text input disabled.' });
        }
        else if (!text || text.length < 1) {
            return Promise.reject({ code: 'MESSAGE_SEND_INVALID', message: 'Invalid message.' });
        }
        else if (this.session.chatInstance.getChatFeatures() &&
            this.session.chatInstance.getChatFeatures().maxCharacterLimit &&
            text.length > this.session.chatInstance.getChatFeatures().maxCharacterLimit) {
            return Promise.reject({ code: 'MESSAGE_SEND_MAX_LENGTH', message: 'Max message character length exceeded.' });
        }
        const id = this.session.chatInstance.getNextMessageId();
        const message = {
            event: 'newMessage',
            data: {
                access_token: token,
                messageText: text,
                messageId: id.toString(),
            },
        };
        return this.sendUserMessage(id, message);
    }
    sendUserMessage(id, message) {
        if (this.getWebSocketState() !== WebSocket.OPEN) {
            return Promise.reject({
                code: 'INVALID_CHAT_STATUS',
                message: 'Invalid websocket connection state.',
            });
        }
        this.session.chatInstance.addToSentMessages(id, message);
        return new Promise((resolve, reject) => {
            this.ws.send(JSON.stringify(message));
            const timeoutId = setTimeout(() => {
                this.messageCallbacks.delete(id);
                reject({ code: 'NO_ACKNOWLEDGEMENT' });
            }, this.session.chatInstance.getAcknowledgementTimeout());
            this.messageCallbacks.set(id, { resolve, reject, timeoutId });
        });
    }
    sendMessageInternal(data) {
        if (this.getWebSocketState() !== WebSocket.OPEN &&
            this.getWebSocketState() !== WebSocket.CLOSING) {
            this.session.chatInstance.emitError('INVALID_CHAT_STATUS', 'Invalid websocket connection state.');
            return;
        }
        this.ws.send(JSON.stringify(data));
    }
    getWebSocketState() {
        if (!this.ws) {
            return WebSocket.CLOSED;
        }
        return this.ws.readyState;
    }
    establishConnection() {
        var _a;
        if (this.ws) {
            this.closeWebsocket(this.session.chatInstance.getCurrentConnectionState() === ConnectionState.RECONNECTING ? false : true);
            this.cleanUpWebsocket();
            this.cleanUpPromises();
        }
        this.pongReceived = true;
        if (!this.session.config.webSocketEnvironment) {
            this.session.chatInstance.emitStatusChange('', this.providerIdentifier, ConnectionState.DISCONNECTED, {
                state: ChatState.COMPLETE,
                endedBy: 'ERROR',
            });
            throw { code: 'METHOD_ERROR', message: 'Invalid environment.' };
        }
        let url = (_a = this.session.chatInstance.getChatUrl()) !== null && _a !== void 0 ? _a : this.session.config.webSocketEnvironment + '/digital/v3/chat/ws/platform';
        if (this.session.chatInstance.getCurrentConnectionState() === ConnectionState.RECONNECTING &&
            this.session.config.useMocks.mockReconnectWebsocketFail) {
            url = 'wss://thisisabadurlthatisdesignedtofail.mock/';
        }
        this.ws = new WebSocket(url, this.latestToken);
        this.startChatPromise = new Promise((resolve, reject) => {
            this.startChatPromiseHandlers = { resolve, reject };
        });
        this.ws.onopen = this.onOpen.bind(this);
        this.ws.onclose = this.onClose.bind(this);
        this.ws.onmessage = this.onMessage.bind(this);
        this.ws.onerror = this.onError.bind(this);
        if (this.establishConnectionTimeout) {
            clearTimeout(this.establishConnectionTimeout);
        }
        this.establishConnectionTimeout = setTimeout(() => {
            if (!this.startChatPromiseHandlers) {
                return;
            }
            if (this.session.chatInstance.getCurrentConnectionState() !== ConnectionState.RECONNECTING) {
                this.session.chatInstance.emitStatusChange('', this.providerIdentifier, ConnectionState.DISCONNECTED, {
                    state: ChatState.COMPLETE,
                    endedBy: 'ERROR',
                    errorCode: 'CONNECTION_FAILURE',
                });
                this.rejectStartChatPromise({
                    code: 'CONNECTION_FAILURE',
                    message: 'Websocket connection timeout.',
                });
                this.closeWebsocket(true);
            }
            else {
                this.closeWebsocket(false);
            }
        }, this.session.chatInstance.getConnectionTimeout());
        if (this.session.config.useMocks) {
            if (this.session.config.useMocks.mockBadFeatureUpdate) {
                setTimeout(() => {
                    this.onMessage({
                        data: JSON.stringify({
                            event: 'featureUpdate',
                            data: undefined,
                        }),
                    });
                }, 500);
            }
            else if (this.session.config.useMocks.mockFeatures) {
                setTimeout(() => {
                    var _a, _b, _c;
                    this.onMessage({
                        data: JSON.stringify({
                            event: 'featureUpdate',
                            data: {
                                features: {
                                    allowTextInput: (_a = this.session.config.useMocks.mockNoTextInput) !== null && _a !== void 0 ? _a : true,
                                    allowEventInput: (_b = this.session.config.useMocks.mockNoEventInput) !== null && _b !== void 0 ? _b : true,
                                    maxCharacterLimit: 100,
                                    supportsMessageAcknowledgements: (_c = this.session.config.useMocks.mockSupportsAcknowledgements) !== null && _c !== void 0 ? _c : false,
                                    supportsProviderIsTyping: true,
                                },
                                gatewaySettings: {
                                    isTypingInterval: 5000,
                                    pingInterval: 3000,
                                    pingRetryLimit: 10,
                                    reconnectionTimeout: 30000,
                                    reconnectionInterval: 10000,
                                    chatURL: '',
                                },
                            },
                        }),
                    });
                }, 500);
            }
            if (this.session.config.useMocks.mockBadParticipantUpdate) {
                setTimeout(() => {
                    this.onMessage({
                        data: JSON.stringify({
                            event: 'participantUpdate',
                            data: undefined,
                        }),
                    });
                }, 750);
            }
            if (this.session.config.useMocks.mockEWTNotification) {
                setTimeout(() => {
                    this.onMessage({
                        data: JSON.stringify({
                            event: 'notification',
                            data: {
                                type: 'ESTIMATED_WAIT',
                                secondsRemaining: '100',
                                timestamp: new Date().getMilliseconds().toString(),
                            },
                        }),
                    });
                }, 100);
            }
            if (this.session.config.useMocks.mockIdleTimeoutNotification) {
                setTimeout(() => {
                    this.onMessage({
                        data: JSON.stringify({
                            event: 'notification',
                            data: {
                                type: 'IDLE_TIMEOUT',
                                secondsRemaining: '60',
                                timestamp: new Date().getMilliseconds().toString(),
                            },
                        }),
                    });
                }, 5000);
            }
            if (this.session.config.useMocks.mockSessionTimeoutNotification) {
                setTimeout(() => {
                    this.onMessage({
                        data: JSON.stringify({
                            event: 'notification',
                            data: {
                                type: 'SESSION_TIMEOUT',
                                secondsRemaining: '60',
                                timestamp: new Date().getMilliseconds().toString(),
                            },
                        }),
                    });
                }, 10000);
            }
            if (this.session.config.useMocks.mockUnexpectedWebsocketClose) {
                if (this.session.chatInstance.getCurrentConnectionState() !== ConnectionState.RECONNECTING) {
                    setTimeout(() => {
                        this.closeWebsocket(false);
                    }, 5000);
                }
            }
        }
        return this.startChatPromise;
    }
    resolveStartChatPromise() {
        if (this.startChatPromiseHandlers) {
            this.startChatPromiseHandlers.resolve();
            this.cleanupStartChatPromise();
        }
    }
    rejectStartChatPromise(reason) {
        if (this.startChatPromiseHandlers) {
            this.startChatPromiseHandlers.reject(reason);
            this.cleanupStartChatPromise();
        }
    }
    cleanupStartChatPromise() {
        this.startChatPromiseHandlers = null;
        this.startChatPromise = null;
    }
    resolveCloseChatPromise() {
        if (this.endChatPromiseHandlers) {
            this.endChatPromiseHandlers.resolve();
            this.cleanupCloseChatPromise();
        }
    }
    rejectCloseChatPromise(reason) {
        if (this.endChatPromiseHandlers) {
            this.endChatPromiseHandlers.reject(reason);
            this.cleanupCloseChatPromise();
        }
    }
    cleanupCloseChatPromise() {
        this.endChatPromise = null;
        this.endChatPromiseHandlers = null;
    }
    handleClosure(event) {
        if (this.session.config.useMocks.mockSurveyNotification) {
            this.onMessage({
                data: JSON.stringify({
                    event: 'notification',
                    data: {
                        type: 'SURVEY_REQUEST',
                        url: 'http://somerandomsurveyurl.com/',
                        label: 'Take our survey!',
                        content: 'Help us improve our products by completing a short survey of about your experience.',
                        timestamp: new Date().getMilliseconds().toString(),
                    },
                }),
            });
        }
        const [connectionState, chatStatus] = this.session.chatInstance.getCurrentStates();
        if (this.startChatPromiseHandlers && connectionState === ConnectionState.CONNECTING) {
            this.rejectStartChatPromise({
                code: 'CONNECTION_FAILURE',
                message: 'Websocket closed before session established.',
            });
        }
        if (this.endChatPromiseHandlers) {
            this.resolveCloseChatPromise();
        }
        this.cleanUpWebsocket();
        this.cleanUpPromises();
        if (!this.purposefulClose && connectionState !== ConnectionState.CONNECTING) {
            this.startReconnection();
        }
        else {
            this.endReconnection();
            if (connectionState !== ConnectionState.DISCONNECTED ||
                (chatStatus.state !== ChatState.COMPLETE && chatStatus.state !== ChatState.ERROR)) {
                this.session.chatInstance.emitStatusChange('', this.providerIdentifier, ConnectionState.DISCONNECTED, {
                    state: ChatState.COMPLETE,
                });
            }
        }
        this.purposefulClose = false;
    }
    onError(event) {
        this.handleClosure(undefined);
    }
    onClose(event) {
        this.handleClosure(event);
    }
    onOpen(event) {
        this.setPingInterval();
        if (this.session.config.useMocks.mockRestoreChat) {
            this.sendRequestChat(this.latestToken, this.providerIdentifier.providerId, 'WEB', this.requestParams);
        }
        else {
            if (this.sessionId) {
                this.sendRestoreChat(this.latestToken, this.sessionId);
            }
            else {
                this.sendRequestChat(this.latestToken, this.providerIdentifier.providerId, 'WEB', this.requestParams);
            }
        }
    }
    onMessage(json) {
        try {
            this.webSocketMessageHandler(json);
        }
        catch (e) {
            this.session.chatInstance.emitError('MESSAGE_RECEIVE_FAILURE', 'BAD WEBSOCKET DATA');
        }
    }
    rejectPendingMessages() {
        for (let [id, callback] of this.messageCallbacks) {
            clearTimeout(callback.timeoutId);
            callback.reject({ code: 'METHOD_ERROR', message: 'Websocket closed while message is pending.' });
        }
        this.messageCallbacks.clear();
    }
    cleanUpWebsocket() {
        if (this.ws) {
            this.ws.onclose = null;
            this.ws.onerror = null;
            this.ws.onmessage = null;
            this.ws.onopen = null;
            this.ws = null;
        }
    }
    cleanUpPromises() {
        this.cleanupCloseChatPromise();
        this.cleanupStartChatPromise();
        this.rejectPendingMessages();
        window.clearInterval(this.pingInterval);
    }
    closeWebsocket(purposeful) {
        if (this.ws) {
            this.purposefulClose = purposeful;
            this.ws.close(1000, 'Normal closure');
        }
    }
    setPingInterval() {
        if (this.pingInterval) {
            window.clearInterval(this.pingInterval);
        }
        this.pingInterval = window.setInterval(() => {
            const connection = this.session.chatInstance.getCurrentConnectionState();
            if (connection !== ConnectionState.CONNECTED && connection !== ConnectionState.CONNECTING) {
                window.clearInterval(this.pingInterval);
                return;
            }
            this.sendPing();
            if (!this.pongReceived) {
                if (this.missedPongs < this.session.chatInstance.getPingLimit()) {
                    this.missedPongs++;
                    return;
                }
                this.closeWebsocket(false);
                window.clearInterval(this.pingInterval);
                return;
            }
            this.missedPongs = 0;
            this.pongReceived = false;
        }, this.session.chatInstance.getPingInterval());
    }
    signalPongReceived() {
        if (this.session.config.useMocks.mockPingTimeout) {
            return;
        }
        this.pongReceived = true;
    }
    startReconnection() {
        if (this.reconnectInterval) {
            return;
        }
        if (this.sessionId.length < 1) {
            this.endReconnection();
            this.cleanUpPromises();
            this.session.chatInstance.emitStatusChange('', this.providerIdentifier, ConnectionState.DISCONNECTED, {
                state: ChatState.COMPLETE,
                endedBy: 'ERROR',
                errorCode: 'CONNECTION_FAILURE',
            });
            this.closeWebsocket(true);
            return;
        }
        this.session.chatInstance.emitStatusChange('', this.providerIdentifier, ConnectionState.RECONNECTING, {
            state: ChatState.ACTIVE,
        });
        this.reconnectTimeout = setTimeout(() => {
            this.endReconnection();
            this.cleanUpPromises();
            this.session.chatInstance.emitStatusChange('', this.providerIdentifier, ConnectionState.DISCONNECTED, {
                state: ChatState.COMPLETE,
                endedBy: 'ERROR',
                errorCode: 'CONNECTION_FAILURE',
            });
            this.closeWebsocket(true);
        }, this.session.chatInstance.getReconnectionTimeout());
        this.reconnectInterval = setInterval(() => {
            this.establishConnection();
        }, this.session.chatInstance.getReconnectionInterval());
    }
    endReconnection() {
        clearTimeout(this.reconnectTimeout);
        clearInterval(this.reconnectInterval);
        this.reconnectInterval = null;
        this.reconnectTimeout = null;
    }
    messageAcknowledgementReceived(id) {
        if (this.session.config.useMocks.mockNoAcknowledgement) {
            return;
        }
        const callback = this.messageCallbacks.get(id);
        if (!callback) {
            return;
        }
        clearTimeout(callback.timeoutId);
        let status = 'SENT';
        if (this.session.chatInstance.getChatFeatures() &&
            this.session.chatInstance.getChatFeatures().supportsMessageAcknowledgements) {
            status = 'DELIVERED';
        }
        callback.resolve({
            id: id.toString(),
            status: status,
            timestamp: new Date(),
        });
        this.messageCallbacks.delete(id);
    }
}
//# sourceMappingURL=provider-abstract.js.map